import React, { ReactElement, ReactNode } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton/IconButton';
import { Close } from '@mui/icons-material';
import MfButton from '../Button/MfButton';
import styles from './MfDialog.module.scss';

interface Props {
  children: ReactNode;
  open: boolean;
  onClose: () => void;
  title?: string;
  cancelButton?: boolean;
  onCancel?: () => void;
  onOk?: (() => void) | false;
  okLabel?: string;
  cancelLabel?: string;
  processing?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | false;
  fullScreen?: boolean;
  actionButtons?: ReactElement | null;
  okButtonRef?: React.RefObject<HTMLButtonElement>;
}

const MfDialog = ({
  children,
  onCancel,
  onClose,
  open,
  cancelButton,
  onOk,
  title,
  cancelLabel,
  okLabel,
  processing,
  maxWidth,
  fullScreen,
  actionButtons,
  okButtonRef,
  ...rest
}: Props) => {
  const theme = useTheme();
  let fs = useMediaQuery(theme.breakpoints.down('md'));
  if (fullScreen || fullScreen === false) {
    fs = fullScreen;
  }
  return (
    <Dialog
      maxWidth={maxWidth}
      fullWidth
      fullScreen={fs}
      open={open}
      onClose={onClose}
      {...rest}
    >
      <DialogTitle>
        <div className={styles.titleWrapper}>
          {title}
          <IconButton size="medium" onClick={onClose}>
            <Close />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent style={{ width: '100%' }}>{children}</DialogContent>
      <DialogActions style={{ padding: '8px 24px 16px' }}>
        {actionButtons}
        {cancelButton && (
          <MfButton
            onClick={typeof onCancel === 'function' ? onCancel : onClose}
            color="secondary"
            size="small"
            disabled={processing}
            variant="contained"
          >
            <span className="text-transform-none">{cancelLabel}</span>
          </MfButton>
        )}
        {typeof onOk === 'function' && (
          <MfButton
            ref={okButtonRef}
            onClick={typeof onOk === 'function' ? onOk : onClose}
            color="primary"
            size="small"
            disabled={processing}
            variant="contained"
          >
            <span className="text-transform-none">{okLabel}</span>
          </MfButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

MfDialog.defaultProps = {
  title: '',
  cancelButton: false,
  onCancel: () => undefined,
  onOk: false,
  okLabel: 'Ок',
  cancelLabel: 'Отмена',
  processing: false,
  maxWidth: 'md',
  fullScreen: undefined,
  actionButtons: null,
  okButtonRef: null,
};

export default MfDialog;
